import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import IndividualCard from '../../components/IndividualCard/IndividualCard';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './HomePage.module.css';
import config from '../../config/config';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredVerse, setFeaturedVerse] = useState({
    reference: '2 Timothy 3:16-17',
    verses: 'All scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness: That the man of God may be perfect, throughly furnished unto all good works.',
    explanation: 'This verse reminds us of the importance and purpose of Scripture in our lives.',
    title: 'The Power and Purpose of Scripture',
    content: 'This passage affirms the divine inspiration and practical value of Scripture.',
    date: '2025-04-19',
    author: 'Apostle Paul',
    references: ['2 Timothy 3:16-17', '2 Peter 3:15-16']
  });

  useEffect(() => {
    // Fetch featured Bible verses
    const fetchFeaturedVerses = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/featured-verses`);
        if (response.data && response.data.length > 0) {
          setFeaturedVerse(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching featured verses:', error);
        
        // Try fetching from regular verses as fallback
        try {
          const fallbackResponse = await axios.get(`${config.API_BASE_URL}/verses`);
          if (fallbackResponse.data && fallbackResponse.data.length > 0) {
            setFeaturedVerse(fallbackResponse.data[0]);
          }
        } catch (fallbackError) {
          console.error('Error fetching fallback verses:', fallbackError);
          
          // Only use mock data if enabled in config
          if (!config.USE_MOCK_DATA) {
            setFeaturedVerse({
              reference: 'Error',
              verses: 'Could not load Bible verse',
              explanation: 'Please check your connection to the backend server',
              title: 'Error Loading Verse',
              content: 'There was an error loading the featured verse. Please check your connection to the backend server.',
              date: new Date().toISOString().split('T')[0],
              author: 'System',
              references: []
            });
          }
        }
      }
    };

    fetchFeaturedVerses();
  }, []);

  return (
    <div className={styles['home-page']}>
      <section className={styles['welcome-section']}>
        <h1>Welcome to Christ Centered Baptist</h1>
        <p className={styles['welcome-message']}>
          We are dedicated to teaching Christ through the Bible. Our mission is to share the message of salvation
          and help believers grow in their faith through the study of God's Word.
        </p>
      </section>

      <section className={styles['page-links-section']}>
        <h2>Explore Our Resources</h2>
        <div className={styles['links-grid']}>
          <div 
            className={`${styles['link-item']} ${styles['clickable-card']}`}
            onClick={() => navigate('/salvation')}
          >
            <h3>Salvation</h3>
            <p>Learn about God's plan of salvation through Jesus Christ.</p>
          </div>
          <div 
            className={`${styles['link-item']} ${styles['clickable-card']}`}
            onClick={() => navigate('/hymns')}
          >
            <h3>Hymns</h3>
            <p>Explore a collection of traditional hymns and their histories.</p>
          </div>
          <div 
            className={`${styles['link-item']} ${styles['clickable-card']}`}
            onClick={() => navigate('/armor-of-god')}
          >
            <h3>Armor of God</h3>
            <p>Understand the spiritual armor described in Ephesians 6.</p>
          </div>
          <div 
            className={`${styles['link-item']} ${styles['clickable-card']}`}
            onClick={() => navigate('/safety-verses')} // Points to existing safety verses page
          >
            <h3>Safety Team</h3>
            <p>Information and resources for the Safety Team.</p> 
          </div>
          <div
            className={`${styles['link-item']} ${styles['clickable-card']}`}
            onClick={() => navigate('/about')}
          >
            <h3>About</h3>
            <p>Find out more about our church's beliefs and mission.</p>
          </div>
        </div>
      </section>

      <section className={styles['featured-verse-section']}>
        <h2>Featured Bible Verse</h2>
        <div 
          className={styles['clickable-card']} 
          onClick={() => navigate(`/bible/${encodeURIComponent(featuredVerse.reference)}`)}
        >
          <IndividualCard
            reference={featuredVerse.reference}
            verses={featuredVerse.verses}
            explanation={featuredVerse.explanation}
          />
        </div>
      </section>

      <ContactUs />
    </div>
  );
};

export default HomePage;
